import { Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface CredentialsDelivered {
  sms: boolean;
  email: boolean;
}

@Component({
  selector: 'app-temp-password-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm"
         role="dialog" aria-modal="true" aria-labelledby="tempPasswordTitle">
      <div class="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 relative">
        <!-- Success halo + icon -->
        <div class="flex justify-center mb-4">
          <div class="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center ring-8 ring-green-50">
            <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
            </svg>
          </div>
        </div>

        <h2 id="tempPasswordTitle" class="text-xl font-bold text-center text-gray-900 mb-1">{{ heading }}</h2>
        <p class="text-sm text-center text-gray-500 mb-6">{{ subheading }}</p>

        <!-- OTP-style digit boxes -->
        <div class="flex justify-center gap-2 sm:gap-3 mb-5">
          @for (d of digits(); track $index) {
            <div class="w-12 h-14 sm:w-14 sm:h-16 bg-gradient-to-b from-gray-50 to-gray-100
                        border-2 border-gray-200 rounded-xl flex items-center justify-center
                        text-3xl sm:text-4xl font-mono font-bold text-blue-700 tracking-widest">
              {{ d }}
            </div>
          }
        </div>

        <!-- Copy button -->
        <div class="flex justify-center mb-5">
          <button type="button" (click)="copy()"
                  class="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-50 hover:bg-blue-100
                         text-blue-700 text-sm font-medium transition-colors">
            @if (copied()) {
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
              <span>Copied!</span>
            } @else {
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
              </svg>
              <span>Copy password</span>
            }
          </button>
        </div>

        <!-- Delivery status pills -->
        @if (credentialsDelivered) {
          <div class="flex flex-wrap justify-center gap-2 mb-3">
            <!-- SMS pill: green/sent · grey/no-phone · red/failed -->
            @if (smsState() === 'sent') {
              <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ring-1 bg-green-50 text-green-700 ring-green-200">
                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z"/></svg>
                SMS sent
              </span>
            } @else if (smsState() === 'missing') {
              <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ring-1 bg-gray-50 text-gray-600 ring-gray-200">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.95.68l1.5 4.49a1 1 0 01-.5 1.21l-2.26 1.13a11 11 0 005.52 5.52l1.13-2.26a1 1 0 011.21-.5l4.49 1.5a1 1 0 01.68.95V19a2 2 0 01-2 2h-1C9.72 21 3 14.28 3 6V5z"/></svg>
                No phone on file
              </span>
            } @else {
              <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ring-1 bg-red-50 text-red-700 ring-red-200">
                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M10 8.6L5.7 4.3 4.3 5.7 8.6 10l-4.3 4.3 1.4 1.4L10 11.4l4.3 4.3 1.4-1.4L11.4 10l4.3-4.3-1.4-1.4z"/></svg>
                SMS failed
              </span>
            }

            <!-- Email pill: green/sent · grey/no-email · red/failed -->
            @if (emailState() === 'sent') {
              <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ring-1 bg-green-50 text-green-700 ring-green-200">
                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z"/></svg>
                Email sent
              </span>
            } @else if (emailState() === 'missing') {
              <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ring-1 bg-gray-50 text-gray-600 ring-gray-200">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                No email on file
              </span>
            } @else {
              <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ring-1 bg-red-50 text-red-700 ring-red-200">
                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M10 8.6L5.7 4.3 4.3 5.7 8.6 10l-4.3 4.3 1.4 1.4L10 11.4l4.3 4.3 1.4-1.4L11.4 10l4.3-4.3-1.4-1.4z"/></svg>
                Email failed
              </span>
            }
          </div>
        }

        <!-- WhatsApp share fallback -->
        @if (showWhatsAppShare()) {
          <div class="flex justify-center mb-4">
            <a [href]="whatsAppLink()" target="_blank" rel="noopener noreferrer"
               class="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700
                      text-white text-sm font-medium transition-colors">
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.52 3.48A11.93 11.93 0 0012.04 0C5.5 0 .17 5.34.17 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.32-1.66a11.86 11.86 0 005.7 1.45h.01c6.55 0 11.9-5.34 11.9-11.9 0-3.18-1.24-6.17-3.41-8.41zM12.04 21.78h-.01a9.86 9.86 0 01-5.02-1.37l-.36-.21-3.75.98 1-3.66-.23-.38a9.85 9.85 0 01-1.51-5.25c0-5.45 4.44-9.88 9.88-9.88a9.86 9.86 0 019.88 9.88c0 5.45-4.43 9.89-9.88 9.89zm5.42-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.39-1.47-.88-.78-1.47-1.75-1.65-2.05-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.6-.92-2.2-.24-.58-.49-.5-.67-.51l-.57-.01a1.1 1.1 0 00-.8.37c-.27.3-1.05 1.02-1.05 2.48s1.07 2.88 1.22 3.08c.15.2 2.12 3.24 5.13 4.55.72.31 1.27.5 1.71.64.72.23 1.37.2 1.89.12.58-.09 1.76-.72 2-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35z"/>
              </svg>
              Share via WhatsApp
            </a>
          </div>
        }

        @if (username) {
          <p class="text-xs text-center text-gray-500 mb-4">
            Username: <strong class="text-gray-800 font-mono">{{ username }}</strong>
          </p>
        }

        <p class="text-xs text-center text-gray-500 mb-6 leading-relaxed">
          {{ footer }}
        </p>

        <button type="button" (click)="close.emit()"
                class="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors">
          Done
        </button>
      </div>
    </div>
  `
})
export class TempPasswordModalComponent {
  @Input({ required: true }) password = '';
  @Input() username: string | null = null;
  @Input() phone: string | null = null;
  @Input() email: string | null = null;
  @Input() committeeName: string | null = null;
  @Input() credentialsDelivered: CredentialsDelivered | null = null;
  @Input() heading = 'Account ready';
  @Input() subheading = 'A temporary password has been generated.';
  @Input() footer = 'Member will be asked to change this password on first login. They can log in with their phone number, NIC, or email.';

  @Output() close = new EventEmitter<void>();

  copied = signal(false);
  digits = computed(() => this.password.split(''));

  smsState = computed<'sent' | 'missing' | 'failed'>(() => {
    if (this.credentialsDelivered?.sms) return 'sent';
    return this.phone && this.phone.trim() ? 'failed' : 'missing';
  });

  emailState = computed<'sent' | 'missing' | 'failed'>(() => {
    if (this.credentialsDelivered?.email) return 'sent';
    return this.email && this.email.trim() ? 'failed' : 'missing';
  });

  showWhatsAppShare = computed(() => this.smsState() === 'failed');

  whatsAppLink = computed(() => {
    const phone = (this.phone ?? '').replace(/[^\d]/g, '');
    const committee = this.committeeName ?? 'Committee';
    const usernameLine = this.username ? `Username: ${this.username}. ` : '';
    const msg = `Your ${committee} login was reset. ${usernameLine}Temporary password: ${this.password}. Please change it on next login.`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
  });

  async copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.password);
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 1500);
    } catch {
      this.copied.set(false);
    }
  }
}
